'use client';
import React, { useState } from 'react';
import AdminKPIGrid from './AdminKPIGrid';
import AdminKanban from './AdminKanban';
import AdminRevenueChart from './AdminRevenueChart';
import AdminComplaintsTable from './AdminComplaintsTable';
import AdminCleanerTable from './AdminCleanerTable';
import AdminChatPanel from './AdminChatPanel';
import AdminOnboardingReview from './AdminOnboardingReview';

type Tab = 'overview' | 'kanban' | 'complaints' | 'cleaners' | 'onboarding' | 'chat';

export default function AdminDashboardContent() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const tabs: { id: Tab; label: string; badge?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'kanban', label: 'Job Board' },
    { id: 'complaints', label: 'Complaints', badge: 3 },
    { id: 'cleaners', label: 'Cleaners' },
    { id: 'onboarding', label: 'Onboarding' },
    { id: 'chat', label: 'Live Chat' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Operations Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Sunday, May 17, 2026 · Last updated 2 min ago</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors active:scale-95">
            Export Report
          </button>
          <button className="px-4 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150">
            + Create Job
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <AdminKPIGrid />

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={`admintab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${activeTab === tab.id ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {tab.label}
            {tab.badge && (
              <span className="bg-danger text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <AdminRevenueChart />
          </div>
          <div className="xl:col-span-1">
            <AdminActivityFeed />
          </div>
        </div>
      )}
      {activeTab === 'kanban' && <AdminKanban />}
      {activeTab === 'complaints' && <AdminComplaintsTable />}
      {activeTab === 'cleaners' && <AdminCleanerTable />}
      {activeTab === 'onboarding' && <AdminOnboardingReview />}
      {activeTab === 'chat' && <AdminChatPanel />}
    </div>
  );
}

function AdminActivityFeed() {
  const events = [
    { id: 'ev-001', text: 'Maria Gonzalez completed Deep Clean at 142 Maple St', time: '12 min ago', color: 'bg-success-bg text-success' },
    { id: 'ev-002', text: 'New complaint submitted for job #job-004', time: '34 min ago', color: 'bg-danger-bg text-danger' },
    { id: 'ev-003', text: 'Sarah Mitchell booked a Standard Clean for Jun 3', time: '1 hr ago', color: 'bg-info-bg text-info' },
    { id: 'ev-004', text: 'Marcus Johnson started job at 87 Riverside Dr', time: '2 hrs ago', color: 'bg-primary/10 text-primary' },
    { id: 'ev-005', text: 'Priya Patel confirmed her May 21 appointment', time: '3 hrs ago', color: 'bg-info-bg text-info' },
    { id: 'ev-006', text: 'New cleaner application from Devon Richards', time: '5 hrs ago', color: 'bg-warning-bg text-warning' },
  ];

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden h-full">
      <div className="px-4 py-3 border-b border-border">
        <p className="text-sm font-bold text-foreground">Live Activity</p>
      </div>
      <div className="divide-y divide-border">
        {events.map((ev) => (
          <div key={ev.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${ev.color.split(' ')[0]}`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-foreground leading-relaxed">{ev.text}</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">{ev.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}